/**
 * PATH: packages/quote-builder/src/grid/formulaCatalog.ts
 * FILE_ID: QB-GRID-FORMULACATALOG-001
 * PURPOSE: The discoverable function catalogue for A1 spreadsheet
 *   surfaces — curated names + signatures + one-line docs, fragment
 *   matching for autocomplete, and "which function am I inside?"
 *   context for signature hints.
 * WHY: Operator 2026-07-06 — "=SUM … is not there": every function
 *   existed in the engine but NOTHING in the UI said so. This module is
 *   the single source the formula bar (and future in-cell editors) use
 *   to make the engine's catalogue visible.
 * FLOW:
 *   "=su" + caret → suggestFormulaFunctions → [SUM, SUMPRODUCT…]
 *   "=SUM(A1," + caret → getActiveFunctionContext → SUM signature hint
 * DEPENDENCIES: ./formulaRepair (string masking)
 */
import { maskA1StringLiterals } from './formulaRepair'

export interface FormulaFunctionDoc {
  name: string
  /** Human signature, e.g. "SUM(number1, [number2], …)". */
  signature: string
  /** One-line description shown in the menu. */
  description: string
}

/**
 * Curated catalogue: every entry is backed by the shared engine
 * (FormulaJS + custom IF handling). Ordered roughly by usage frequency
 * so the unfiltered menu leads with the everyday functions.
 */
export const FORMULA_FUNCTIONS_CATALOG: FormulaFunctionDoc[] = [
  { name: 'SUM', signature: 'SUM(number1, [number2], …)', description: 'Add numbers or a range: =SUM(A1:A5)' },
  { name: 'AVERAGE', signature: 'AVERAGE(number1, [number2], …)', description: 'Mean of numbers or a range' },
  { name: 'MIN', signature: 'MIN(number1, [number2], …)', description: 'Smallest value' },
  { name: 'MAX', signature: 'MAX(number1, [number2], …)', description: 'Largest value' },
  { name: 'COUNT', signature: 'COUNT(value1, [value2], …)', description: 'Count numeric values' },
  { name: 'IF', signature: 'IF(condition, then, [else])', description: 'Conditional: =IF(A1>5,"yes","no")' },
  { name: 'ROUND', signature: 'ROUND(number, digits)', description: 'Round to N decimal places' },
  { name: 'ROUNDUP', signature: 'ROUNDUP(number, digits)', description: 'Round away from zero' },
  { name: 'ROUNDDOWN', signature: 'ROUNDDOWN(number, digits)', description: 'Round toward zero' },
  { name: 'ABS', signature: 'ABS(number)', description: 'Absolute value' },
  { name: 'SQRT', signature: 'SQRT(number)', description: 'Square root' },
  { name: 'POWER', signature: 'POWER(base, exponent)', description: 'Raise to a power' },
  { name: 'PRODUCT', signature: 'PRODUCT(number1, [number2], …)', description: 'Multiply numbers or a range' },
  { name: 'CEILING', signature: 'CEILING(number, significance)', description: 'Round up to a multiple' },
  { name: 'FLOOR', signature: 'FLOOR(number, significance)', description: 'Round down to a multiple' },
  { name: 'MOD', signature: 'MOD(number, divisor)', description: 'Remainder after division' },
  { name: 'CONCATENATE', signature: 'CONCATENATE(text1, [text2], …)', description: 'Join text (or use &)' },
  { name: 'UPPER', signature: 'UPPER(text)', description: 'Uppercase text' },
  { name: 'LOWER', signature: 'LOWER(text)', description: 'Lowercase text' },
  { name: 'TRIM', signature: 'TRIM(text)', description: 'Strip extra spaces' },
  { name: 'LEN', signature: 'LEN(text)', description: 'Length of text' },
  { name: 'PI', signature: 'PI()', description: 'The constant π' },
]

const NAME_INDEX = new Map(FORMULA_FUNCTIONS_CATALOG.map((f) => [f.name, f]))

/**
 * Trailing identifier fragment of a composing formula ("=1+su" → "su").
 * String literals are masked first, so typing inside "…" never yields a
 * fragment (mask preserves length → indices map 1:1 to the raw text).
 */
export function extractTrailingIdentifier(text: string): string {
  const masked = maskA1StringLiterals(text)
  // An odd number of quotes after masking = caret inside an OPEN string
  // literal — never suggest functions there.
  if (((masked.match(/"/g) ?? []).length) % 2 === 1) return ''
  const m = /[A-Za-z_][A-Za-z0-9_.]*$/.exec(masked)
  return m ? m[0] : ''
}

/**
 * Pure: fragment → matching catalogue entries. Prefix matches rank
 * before substring matches; empty fragment returns the full catalogue
 * (the "browse" affordance behind the fx button).
 */
export function suggestFormulaFunctions(fragment: string): FormulaFunctionDoc[] {
  const f = fragment.trim().toUpperCase()
  if (f === '') return FORMULA_FUNCTIONS_CATALOG
  const starts: FormulaFunctionDoc[] = []
  const contains: FormulaFunctionDoc[] = []
  for (const doc of FORMULA_FUNCTIONS_CATALOG) {
    if (doc.name.startsWith(f)) starts.push(doc)
    else if (doc.name.includes(f)) contains.push(doc)
  }
  return [...starts, ...contains]
}

/**
 * Pure: which function call is the caret inside (innermost unclosed
 * call)? Powers the signature hint while arguments are being typed.
 * Returns null outside any call or inside an unknown function.
 */
export function getActiveFunctionContext(
  textBeforeCaret: string,
): FormulaFunctionDoc | null {
  const masked = maskA1StringLiterals(textBeforeCaret)
  // Walk left-to-right tracking open calls: push on "NAME(", pop on ")".
  const stack: string[] = []
  const re = /([A-Za-z_][A-Za-z0-9_.]*)\s*\(|(\()|(\))/g
  let m: RegExpExecArray | null
  while ((m = re.exec(masked)) !== null) {
    if (m[1] !== undefined) stack.push(m[1].toUpperCase())
    else if (m[2] !== undefined) stack.push('')
    else if (stack.length > 0) stack.pop()
  }
  for (let i = stack.length - 1; i >= 0; i--) {
    if (stack[i] !== '') return NAME_INDEX.get(stack[i]) ?? null
  }
  return null
}
